/**
 * Navbar.jsx - Navigation Bar Component
 * 
 * A responsive navigation bar component that provides site-wide navigation.
 * Features include:
 * - Responsive design that collapses on mobile devices
 * - Dropdown menu for additional navigation items
 * - Active link highlighting
 * - Click-outside behavior for dropdown
 * - Smooth transitions and hover effects
 * 
 * Dependencies:
 * - react: For component state and lifecycle management
 * - react-router-dom: For client-side routing and navigation
 * - Bootstrap: For responsive design and styling
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom'; // For client-side navigation
import './Navbar.css'; // Component-specific styles
import Logo from '../assets/Images/Logo.png'; // Brand logo image

const Navbar = () => {
  // State to manage dropdown visibility
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Ref for the dropdown container to detect clicks outside
  const dropdownRef = useRef(null);

  /**
   * Toggles the dropdown menu when the dropdown button is clicked
   * @param {Event} e - The click event
   */
  const handleDropdownClick = (e) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  /**
   * Closes the dropdown when clicking outside of it
   * @param {Event} event - The click event
   */
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  // Add event listener for clicks outside the dropdown
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return (
    // Main navigation bar with Bootstrap classes for styling
    <nav className="navbar navbar-expand-lg navbar-light bg-white sticky-top shadow-sm">
      <div className="container">
        {/* Brand/logo section */}
        <Link className="navbar-brand text-decoration-none" to="/">
          <div className="brand-wrapper d-flex align-items-center">
            <img 
              src={Logo} 
              alt="General Pest Control Services Logo" 
              className="navbar-logo" 
            />
            <span className="brand-name fw-bold text-danger text-decoration-none">
              General Pest Control Services Jodhpur
            </span>
          </div>
        </Link>
        {/* Mobile menu button - only visible on small screens */}
        <button 
          className="navbar-toggler" 
          type="button" 
          data-bs-toggle="collapse" 
          data-bs-target="#navbarNavDropdown" 
          aria-controls="navbarNavDropdown" 
          aria-expanded="false" 
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        {/* Collapsible navigation menu */}
        <div className="collapse navbar-collapse" id="navbarNavDropdown">
          <ul className="navbar-nav ms-auto align-items-center">
            {/* Navigation items with active state */}
            <li className="nav-item">
              <NavLink 
                className="nav-link" 
                to="/" 
                end // Ensures this link is only active when the path is exactly "/"
              >
                Home
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink 
                className="nav-link" 
                to="/about"
              >
                About Us
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink 
                className="nav-link" 
                to="/services"
              >
                Services
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink 
                className="nav-link" 
                to="/pricing"
              >
                Pricing
              </NavLink>
            </li>
            
            <li className="nav-item">
              <NavLink 
                className="nav-link" 
                to="/gallery"
              >
                Gallery
              </NavLink>
            </li>
            {/* Dropdown menu for additional navigation items */}
            <li 
              className="nav-item dropdown" 
              ref={dropdownRef}
            >
              <button
                className="nav-link dropdown-toggle border-0 bg-transparent"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  handleDropdownClick(e);
                }}
                onMouseEnter={() => setIsDropdownOpen(true)} // Open on hover (desktop)
                aria-expanded={isDropdownOpen} // For accessibility
                type="button"
                aria-haspopup="true"
                aria-controls="navbarDropdownMenu"
              >
                More
              </button>
              {/* Dropdown menu items */}
              <ul 
                id="navbarDropdownMenu"
                className={`dropdown-menu${isDropdownOpen ? ' show' : ''}`}
                onClick={(e) => e.stopPropagation()} // Prevent click from closing the dropdown
                onMouseLeave={() => setIsDropdownOpen(false)} // Close when mouse leaves
                aria-labelledby="navbarDropdown"
              >
                <li>
                  <NavLink 
                    className="dropdown-item" 
                    to="/contact"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Contact Us
                  </NavLink>
                </li>
                <li>
                  <NavLink 
                    className="dropdown-item" 
                    to="/blog"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Blog
                  </NavLink>
                </li>
                <li>
                  <NavLink 
                    className="dropdown-item" 
                    to="/faq"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    FAQ
                  </NavLink>
                </li>
              </ul>
            </li>
            {/* Book Now button removed as requested */}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
